import { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Spinner,
  Tooltip,
} from '@billda/design-system';
import {
  AccountBalanceWalletIcon,
  GroupIcon,
  SupervisorAccountIcon,
  WarningIcon,
  RefreshIcon,
  HelpOutlineIcon,
} from '@billda/design-system';
import { usePermission, Permissions } from '@billda/permissions-ui';
import { useFeature, Features } from '@billda/features-ui';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { OFFBOARDING_HELP } from '../../../shared/constants/helpContent';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { Routes, routeBuilders } from '../../../routes';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { useUser } from '../hooks/useUser';
import { useOffboardReadiness } from '../hooks/useOffboardReadiness';
import { useStartOffboard } from '../hooks/useStartOffboard';
import { useCompleteOffboard } from '../hooks/useCompleteOffboard';
import { useCancelOffboard } from '../hooks/useCancelOffboard';
import { UserStatus } from '../api/userManagementApi';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { canManageUserOffboarding } from '../utils/roleChangePolicy';

const BLOCKER_ICONS: Record<string, React.ReactNode> = {
  ActivePots: (
    <AccountBalanceWalletIcon aria-hidden="true" className="size-5" />
  ),
  GangMembership: <GroupIcon aria-hidden="true" className="size-5" />,
  GangLeadership: (
    <SupervisorAccountIcon aria-hidden="true" className="size-5" />
  ),
};

const BLOCKER_HINTS: Record<string, string> = {
  ActivePots:
    "This will be resolved when all of the user's pots are approved, paid, or archived",
  GangMembership: 'The user may not be a member of a gang',
  GangLeadership: 'The user may not be a leader of a gang',
};

function OffboardingPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUser(userId ?? null);
  const isFeatureEnabled = useFeature(Features.Offboarding.IsEnabled);
  const hasPermission = usePermission(Permissions.Users.Manage);
  const canManageAdmins = usePermission(Permissions.Users.ManageAdmins);
  const currentUser = useCurrentUser();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const startOffboard = useStartOffboard();
  const completeOffboard = useCompleteOffboard();
  const cancelOffboard = useCancelOffboard();

  const [helpOpen, setHelpOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [revokeAccess, setRevokeAccess] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  if (!isFeatureEnabled || !hasPermission) {
    if (userId) {
      return <Navigate to={routeBuilders.users.detail(userId)} replace />;
    }
    return <Navigate to={Routes.users.manage} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col" data-testid="offboarding-page">
        <div className="px-4 pt-4 sm:px-6">
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Loading...' },
            ]}
          />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex h-full flex-col" data-testid="offboarding-page">
        <div className="px-4 pt-4 sm:px-6">
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Not Found' },
            ]}
          />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="type-body-lg text-content-secondary">
            {error ? 'Failed to load user details.' : 'User not found.'}
          </p>
        </div>
      </div>
    );
  }

  if (
    user.status === UserStatus.Inactive ||
    user.status === UserStatus.InvitationSent
  ) {
    return <Navigate to={routeBuilders.users.detail(userId!)} replace />;
  }

  const isCurrentUser = !!(currentUser && user.id === currentUser.userId);
  if (
    !canManageUserOffboarding({
      roles: user.roles,
      isCurrentUser,
      canManageAdmins,
    })
  ) {
    return <Navigate to={routeBuilders.users.detail(userId!)} replace />;
  }

  const displayName = getUserDisplayName(user);

  const handleStartOffboard = () => {
    startOffboard.mutate(
      { userId: userId!, revokeAccess },
      {
        onSuccess: () => {
          setStartDialogOpen(false);
          setRevokeAccess(false);
          showSnackbar('Offboarding started', 'success');
        },
      }
    );
  };

  const handleCompleteOffboard = () => {
    completeOffboard.mutate(userId!, {
      onSuccess: () => {
        setCompleteDialogOpen(false);
        showSnackbar('Offboarding complete', 'success');
        navigate(routeBuilders.users.detail(userId!));
      },
    });
  };

  const handleCancelOffboard = () => {
    cancelOffboard.mutate(userId!, {
      onSuccess: () => {
        setCancelDialogOpen(false);
        showSnackbar('Offboarding cancelled', 'success');
        navigate(routeBuilders.users.detail(userId!));
      },
    });
  };

  return (
    <PageContentArea
      isPanelOpen={helpOpen}
      panel={
        helpOpen ? (
          <SidePanel
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            title="Help"
            testId="offboarding-help-drawer"
          >
            <HelpPanel helpContent={OFFBOARDING_HELP} />
          </SidePanel>
        ) : undefined
      }
    >
      <div className="flex h-full flex-col" data-testid="offboarding-page">
        <div className="flex items-center justify-between px-4 pb-2 pt-4 sm:px-6">
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              {
                label: displayName,
                to: routeBuilders.users.detail(userId!),
              },
              { label: 'Offboarding' },
            ]}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setHelpOpen(true)}
            aria-label="Help"
            data-testid="offboarding-help-button"
            className="text-content-secondary"
          >
            <HelpOutlineIcon aria-hidden="true" className="size-6" />
          </Button>
        </div>

        <div className="max-w-[720px] flex-1 overflow-auto px-4 py-6 sm:px-6">
          {user.status === UserStatus.Active && (
            <ActiveSection
              displayName={displayName}
              startDialogOpen={startDialogOpen}
              revokeAccess={revokeAccess}
              isPending={startOffboard.isPending}
              onOpenDialog={() => setStartDialogOpen(true)}
              onCloseDialog={() => {
                setStartDialogOpen(false);
                setRevokeAccess(false);
              }}
              onRevokeAccessChange={setRevokeAccess}
              onConfirm={handleStartOffboard}
            />
          )}

          {user.status === UserStatus.Offboarding && (
            <OffboardingSection
              userId={userId!}
              displayName={displayName}
              completeDialogOpen={completeDialogOpen}
              cancelDialogOpen={cancelDialogOpen}
              isCompletePending={completeOffboard.isPending}
              isCancelPending={cancelOffboard.isPending}
              onOpenCompleteDialog={() => setCompleteDialogOpen(true)}
              onCloseCompleteDialog={() => setCompleteDialogOpen(false)}
              onOpenCancelDialog={() => setCancelDialogOpen(true)}
              onCloseCancelDialog={() => setCancelDialogOpen(false)}
              onConfirmComplete={handleCompleteOffboard}
              onConfirmCancel={handleCancelOffboard}
            />
          )}
        </div>
      </div>
    </PageContentArea>
  );
}

interface ActiveSectionProps {
  displayName: string;
  startDialogOpen: boolean;
  revokeAccess: boolean;
  isPending: boolean;
  onOpenDialog: () => void;
  onCloseDialog: () => void;
  onRevokeAccessChange: (value: boolean) => void;
  onConfirm: () => void;
}

function ActiveSection({
  displayName,
  startDialogOpen,
  revokeAccess,
  isPending,
  onOpenDialog,
  onCloseDialog,
  onRevokeAccessChange,
  onConfirm,
}: ActiveSectionProps) {
  return (
    <>
      <p className="type-body-lg mb-6 text-content-secondary">
        Starting offboarding will replace {displayName}&apos;s current role with
        a restricted Offboarding role. They will still be able to submit pots
        and view invoices, but will be unable to book new work or manage teams.
      </p>

      <Button
        variant="danger"
        onClick={onOpenDialog}
        data-testid="offboarding-start-button"
      >
        Start Offboarding
      </Button>

      <Dialog
        open={startDialogOpen}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            onCloseDialog();
          }
        }}
      >
        <DialogContent data-testid="offboarding-start-dialog">
          <DialogHeader>Start offboarding {displayName}?</DialogHeader>
          <DialogBody className="mb-4">
            Their current role will be replaced with a restricted Offboarding
            role. They&apos;ll only be able to submit pots, resubmit rejected
            work, and view invoices. Other actions like booking new work will be
            blocked.
          </DialogBody>
          <label className="flex items-center gap-2 type-body-md">
            <Checkbox
              checked={revokeAccess}
              onCheckedChange={checked =>
                onRevokeAccessChange(checked === true)
              }
              data-testid="offboarding-revoke-access-checkbox"
            />
            Log user out now
          </label>
          <p className="type-body-md ml-6 mt-0.5 text-content-secondary">
            Log the user out immediately. Without this, the changes can take up
            to 15 minutes to take effect.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={onCloseDialog}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onConfirm} disabled={isPending}>
              Start Offboarding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface OffboardingSectionProps {
  userId: string;
  displayName: string;
  completeDialogOpen: boolean;
  cancelDialogOpen: boolean;
  isCompletePending: boolean;
  isCancelPending: boolean;
  onOpenCompleteDialog: () => void;
  onCloseCompleteDialog: () => void;
  onOpenCancelDialog: () => void;
  onCloseCancelDialog: () => void;
  onConfirmComplete: () => void;
  onConfirmCancel: () => void;
}

function OffboardingSection({
  userId,
  displayName,
  completeDialogOpen,
  cancelDialogOpen,
  isCompletePending,
  isCancelPending,
  onOpenCompleteDialog,
  onCloseCompleteDialog,
  onOpenCancelDialog,
  onCloseCancelDialog,
  onConfirmComplete,
  onConfirmCancel,
}: OffboardingSectionProps) {
  const { data: readiness, isLoading, refetch } = useOffboardReadiness(userId);

  const getBlockerIcon = (type: string) => {
    return (
      BLOCKER_ICONS[type] ?? (
        <WarningIcon aria-hidden="true" className="size-5" />
      )
    );
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h6 className="type-h6 font-semibold">Readiness</h6>
        <Button
          onClick={() => refetch()}
          data-testid="offboarding-readiness-refresh"
          variant="ghost"
          size="icon"
          aria-label="Refresh readiness"
          className="size-8"
        >
          <RefreshIcon aria-hidden="true" className="size-4" />
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Spinner size={24} />
        </div>
      )}

      {readiness && readiness.blockers.length > 0 && (
        <ul
          className="mb-6 list-none p-0"
          data-testid="offboarding-blocker-list"
        >
          {readiness.blockers.map((blocker, index) => (
            <li
              key={index}
              data-testid="offboarding-blocker-item"
              className="flex items-start gap-4 py-2"
            >
              <span className="shrink-0 text-content-secondary">
                {getBlockerIcon(blocker.type)}
              </span>
              <span className="min-w-0">
                <span className="block type-body-md">
                  {blocker.description}
                </span>
                {BLOCKER_HINTS[blocker.type] && (
                  <span className="block type-caption text-content-secondary">
                    {BLOCKER_HINTS[blocker.type]}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {readiness && readiness.blockers.length === 0 && (
        <Alert
          severity="success"
          className="mb-6"
          data-testid="offboarding-ready-indicator"
        >
          Ready to complete offboarding
        </Alert>
      )}

      <div className="flex flex-wrap gap-4">
        {readiness?.canComplete ? (
          <Button
            variant="danger"
            onClick={onOpenCompleteDialog}
            disabled={isCompletePending}
            data-testid="offboarding-complete-button"
          >
            Complete Offboarding
          </Button>
        ) : (
          <Tooltip content="Resolve all blockers before completing offboarding">
            <Button
              variant="danger"
              disabled
              data-testid="offboarding-complete-button"
            >
              Complete Offboarding
            </Button>
          </Tooltip>
        )}

        <Button
          variant="outlineWarning"
          onClick={onOpenCancelDialog}
          disabled={isCancelPending}
          data-testid="offboarding-cancel-button"
          className="border-border-warning-strong text-content-warning hover:bg-surface-warning-subtle"
        >
          Cancel Offboarding
        </Button>
      </div>

      <ConfirmDialog
        open={completeDialogOpen}
        title={`Complete offboarding for ${displayName}?`}
        message="This will remove all access to the organisation. Their gang memberships, site assignments, and roles will be removed. Historical records (pots, invoices, bookings) are preserved. This cannot be undone — to restore access, you would need to re-invite them."
        onConfirm={onConfirmComplete}
        onCancel={onCloseCompleteDialog}
        confirmText="Complete Offboarding"
      />

      <ConfirmDialog
        open={cancelDialogOpen}
        title={`Cancel offboarding for ${displayName}?`}
        message="They will be restored to their previous role and regain full access. Any work they were restricted from will become available again."
        onConfirm={onConfirmCancel}
        onCancel={onCloseCancelDialog}
        confirmText="Cancel Offboarding"
      />
    </>
  );
}

export default OffboardingPage;
